import React, { useState, useEffect, ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Switch, Tabs } from 'antd';
import axiosConfig from '../../../../utils/axiosConfig';
import Paragraph from 'antd/lib/typography/Paragraph';
import { darcula } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { EnvironmentSettingsItem } from '../../../../api/environments/interface';
import { SwitchChangeEventHandler } from 'antd/lib/switch';
import { ResponseModel } from '../../../../models/fetchingData';
import { ReactComponent as Javascript } from '../../../../assets/javascript.svg';
import { ReactComponent as NodeDotJs } from '../../../../assets/nodedotjs.svg';
import { ReactComponent as Python } from '../../../../assets/python.svg';
import { ReactComponent as Typescript } from '../../../../assets/typescript.svg';
import { tabLanguageLogos } from '../../../../utils/commonStyles';
import {
  codeForBackendJS,
  codeForBackendPy,
  codeForFrontendJS
} from './syntax';

const Security = (): ReactElement => {
  const { accountId } =
    useParams<{
      accountId: string;
    }>();
  const [clientCredentials, setClientCredentialsState] =
    useState<Record<string, string>[]>();
  const [secureMode, setSecureMode] = useState<boolean>();
  const [secureModeLoading, setSecureModeLoading] = useState(true);
  useEffect(() => {
    axiosConfig('get', `environments`, {}, (response) => {
      const res = response as Record<'data', Record<string, string>[]>;
      setClientCredentialsState(res.data);
    });

    axiosConfig('get', accountId, {}, (response) => {
      const res = response as ResponseModel<EnvironmentSettingsItem>;
      setSecureMode(res.data.secureMode);
      setSecureModeLoading(false);
    });
  }, [accountId]);

  const updateSecureMode: SwitchChangeEventHandler = (checked): void => {
    setSecureModeLoading(true);
    axiosConfig(
      'patch',
      accountId,
      {
        secureMode: checked
      },
      () => {
        setSecureMode(checked);
        setSecureModeLoading(false);
      }
    );
  };

  return (
    <Card
      style={{
        margin: '15px',
        width: '96%'
      }}
      title="Secure Mode"
    >
      <Paragraph>
        With Secure Mode, malicious users cannot impersonate other users on the
        front-end to see their in-app notifications. Steps to activate:
      </Paragraph>
      <Paragraph>
        <b>1. Back-end:</b> Hash the user_id and send it to front-end, e.g.
        after user signs in or the page loads:
      </Paragraph>

      <Tabs defaultActiveKey="1">
        <Tabs.TabPane
          tab={
            <span>
              <NodeDotJs style={tabLanguageLogos} />
              Node.js
            </span>
          }
          key="1"
        >
          <SyntaxHighlighter
            data-testid="backend-js"
            language="javascript"
            style={darcula}
          >
            {codeForBackendJS(clientCredentials)}
          </SyntaxHighlighter>
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={
            <span>
              <Python style={tabLanguageLogos} />
              Python
            </span>
          }
          key="2"
        >
          <SyntaxHighlighter
            data-testid="backend-py"
            language="python"
            style={darcula}
          >
            {codeForBackendPy(clientCredentials)}
          </SyntaxHighlighter>
        </Tabs.TabPane>
      </Tabs>

      <Paragraph>
        <b>2. Front-end:</b> pass the hash value to the NotificationAPI SDK.
      </Paragraph>

      <Tabs defaultActiveKey="1">
        <Tabs.TabPane
          tab={
            <span>
              <Javascript style={tabLanguageLogos} />
              Javascript
            </span>
          }
          key="1"
        >
          <SyntaxHighlighter
            data-testid="frontend-js"
            language="javascript"
            style={darcula}
          >
            {codeForFrontendJS(clientCredentials)}
          </SyntaxHighlighter>
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={
            <span>
              <Typescript style={tabLanguageLogos} />
              Typescript
            </span>
          }
          key="2"
        >
          <SyntaxHighlighter
            data-testid="frontend-ts"
            language="javascript"
            style={darcula}
          >
            {codeForFrontendJS(clientCredentials)}
          </SyntaxHighlighter>
        </Tabs.TabPane>
      </Tabs>

      <Paragraph>
        <b>3. Enable Secure Mode</b>
        <Switch
          title="Toggle Secure Mode"
          checked={secureMode}
          loading={secureModeLoading}
          onChange={updateSecureMode}
          onClick={updateSecureMode}
        />
      </Paragraph>

      <Paragraph>
        Our system will compare the actual and hashed user_id to ensure your
        users have not manipulated it. To verify everything is working, load a
        page with in-app notifications and ensure there are no console errors
        and that notifications are loading.
      </Paragraph>
    </Card>
  );
};
export default Security;
